/*
 * Copyright (c) 2024.
 * The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import UiFilterLabel from "./Filter/UiFilterLabel";
import {MagnifyingGlassIcon} from "@heroicons/react/24/solid";
import {useForm} from "react-hook-form";
import {useDebouncedCallback} from "use-debounce";
import TableFilterRemoveButton
    from "pages/Front/components/Tanstack/components/Filters/components/TableFilterRemoveButton";

type SearchInputField = {
    search: string;
};

interface UiSearchProps {
    label?: string;
    className?: string;
    placeholder?: string;
    onChange?: (value: string) => void;
    value?: string;
    onSubmit?: (data: SearchInputField) => void;
    disabled?: boolean;
}

const UiSearch = (props: UiSearchProps) => {
    const [internalValue, setInternalValue] = useState(props.value ? props.value : "");
    const {t} = useTranslation();
    const {register, handleSubmit} = useForm<SearchInputField>({
        defaultValues: {
            search: props.value,
        },
    });
    const debouncedSubmitHandler = useDebouncedCallback(data => {
        props.onSubmit && props.onSubmit(data);
    }, 300);
    const debouncedChangeHandler = useDebouncedCallback(data => {
        props.onChange && props.onChange(data);
    }, 300);

    const onSubmitHandler = (data: SearchInputField) => {
        console.log("onSubmitHandler", data)
        if (props.onSubmit) {
            debouncedSubmitHandler(data);
        }
    };

    const onChangeHandler = (data: string) => {
        setInternalValue(data);
        if (props.onChange) {
            debouncedChangeHandler(data);
        }
    };

    const removeFiltersHandler = () => {
        debouncedSubmitHandler({search: null});
    };

    useEffect(() => {
        setInternalValue(props.value ? props.value : "");
    }, [props.value]);

    return (
        <div className={clsx("UiSearch flex flex-col w-auto", props.className)}>
            {props.label && (
                <div className={"relative flex flex-row items-center"}>
                    <UiFilterLabel label={props.label} />
                    <TableFilterRemoveButton className={"!relative !right-auto mb-2"} inputValue={props.value} onClick={removeFiltersHandler} />
                </div>
            )}
            <div className={"flex flex-row items-center"}>
                <form
                    className={
                        "SearchItem flex flex-row items-center justify-start max-w-md w-full relative bg-white shadow-sm shadow-black/10 rounded focus-visible:outline-0 group"
                    }
                    onSubmit={handleSubmit(onSubmitHandler)}
                >
                    <input
                        {...register("search")}
                        className={clsx(
                            "rounded inline-flex items-center pl-3.5 pr-16 py-2 text-sm font-medium transition duration-150 group-hover:bg-white group-focus-within:bg-white border border-gray-500 placeholder:text-gray-800 focus:outline focus:outline-1 focus:outline-blue-400 focus:-outline-offset-1",
                            props.value ? "text-blue-400" : "text-dark-800 bg-gray-100",
                            props?.label ? "pr-16" : "pr-[4.5rem]",
                        )}
                        disabled={props.disabled}
                        name={"search"}
                        placeholder={props.placeholder ?? t("Type search string here...")}
                        type="text"
                        value={internalValue}
                        onChange={e => onChangeHandler(e.target.value)}
                    />
                    {!props?.label && (
                        <TableFilterRemoveButton className={"!right-11 border-r pr-1.5 z-10"} inputValue={props.value} onClick={removeFiltersHandler} />
                    )}
                    <button
                        className={
                            "absolute pr-3.5 pl-3.5 right-0 top-0 bottom-0 rounded-tr rounded-br w-auto transition duration-150 text-gray-800 group-hover:text-blue-400 group-focus-within:text-blue-400 hover:!text-white hover:bg-blue-400 hover:outline-1 hover:outline-blue-400 hover:border-blue-400"
                        }
                        disabled={props.disabled}
                        type={"submit"}
                        onClick={handleSubmit(onSubmitHandler)}
                    >
                        <MagnifyingGlassIcon className={"w-4 h-4 z-10 relative"} />
                    </button>
                </form>
            </div>
        </div>
    );
};

export default UiSearch;
